import { AgentAvatar } from "@/components/molecules/agent-avatar";
import { Badge } from "@/components/ui/badge";
import { ToolIcon } from "@/components/molecules/tool-icon";
import type { Agent } from "@/features/agents/types";
import type { Connector } from "@/features/connectors";
import { connectorTypeLabel } from "@/features/connectors/types";
import { useAvailableTools } from "@/features/tools/hooks/use-tools";
import { useToolCatalog } from "@/features/tools/hooks/use-tool-catalog";
import { useTranslation } from "@/features/utils/i18n";
import _ from "lodash";
import { Link, useParams } from "react-router-dom";
import { useMemo } from "react";

interface AgentCardProps {
  agent: Agent;
  connectors?: Connector[];
}

/**
 * An agent on the overview: who it is, where it answers, what it can do.
 *
 * The whole card is a link to the agent (so cmd-click opens a tab), and it
 * fills its grid cell: a card with a two-line role and one with none sit side
 * by side at the same height, their capabilities aligned at the bottom.
 * Capabilities are named, not just drawn — five black glyphs in a row said
 * nothing without hovering each.
 */
export function AgentCard({ agent, connectors }: AgentCardProps) {
  const { t } = useTranslation("agents");
  const { company: clientId } = useParams<{ company: string }>();
  const { data: available = [] } = useAvailableTools();
  const catalog = useToolCatalog();

  const sources = _.uniq(
    connectors
      ?.map((connector) => {
        return connector.configuration.phone_numbers?.length
          ? connector.configuration.phone_numbers
          : [connectorTypeLabel(connector.type)];
      })
      .flat() || [],
  );

  // Get enabled tools for this agent
  const enabledTools = useMemo(() => {
    return available.filter((item) => {
      const clientTool = item.clientTool;
      const agentConfig = clientTool?.per_agents?.[agent.id];

      if (agentConfig?.enabled !== undefined) {
        return agentConfig.enabled;
      }
      return clientTool?.enabled || false;
    });
  }, [available, agent.id]);

  return (
    <Link
      to={`/${clientId}/agents/${agent.id}`}
      className="flex h-full flex-col items-center gap-4 rounded-lg border bg-card p-6 text-center text-card-foreground transition-[border-color,box-shadow] duration-150 outline-none hover:border-ring/40 hover:shadow-xs focus-visible:ring-[3px] focus-visible:ring-ring/40"
    >
      <AgentAvatar avatarId={agent.avatar_id} name={agent.name} size="2xl" />

      <div className="space-y-1">
        <h3 className="text-lg font-semibold">{agent.name}</h3>
        <p
          className={
            agent.role
              ? "text-sm text-muted-foreground"
              : "text-sm italic text-muted-foreground"
          }
        >
          {agent.role || t("agents.view.noRole")}
        </p>
      </div>

      {sources.length > 0 && (
        <div className="flex flex-wrap justify-center gap-1.5">
          {sources.map((source) => (
            <Badge
              key={source}
              variant="outline"
              className={/^\+?\d/.test(source) ? "font-mono" : undefined}
            >
              {source}
            </Badge>
          ))}
        </div>
      )}

      {enabledTools.length > 0 && (
        <ul
          aria-label={t("agents.view.toolsConfiguration")}
          className="mt-auto flex w-full flex-wrap justify-center gap-1.5 border-t pt-4"
        >
          {enabledTools.map((item) => (
            <li
              key={item.tool.id}
              className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground"
            >
              <ToolIcon toolName={item.tool.name} className="size-3.5" />
              {catalog.label(item.tool)}
            </li>
          ))}
        </ul>
      )}
    </Link>
  );
}
